import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type AuthMode = "signin" | "signup" | "forgot-password";
type UserType = "nonprofit" | "corporate" | "host";

const AuthPages = () => {
  const [userType, setUserType] = useState<UserType>("nonprofit");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isWaitlistLoading, setIsWaitlistLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isWaitlistDialogOpen, setIsWaitlistDialogOpen] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+65",
    userType: "",
    remarks: "",
  });
  const { signIn, signUp, requestPasswordReset, session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { toast } = useToast();

  // Get redirect path from URL or session storage
  const redirectPath = searchParams.get("redirect") || sessionStorage.getItem("redirectPath") || "/dashboard";
  
  useEffect(() => {
    if (session) {
      handleRedirect(redirectPath);
    }
  }, [session, redirectPath]);

  useEffect(() => {
    // Store redirect path in session storage when it changes
    if (searchParams.get("redirect")) {
      sessionStorage.setItem("redirectPath", searchParams.get("redirect")!);
    }
  }, [searchParams]);

  const handleRedirect = (path: string) => {
    // Clear redirect path from session storage
    sessionStorage.removeItem("redirectPath");
    navigate(path);
  };

  // Determine auth mode based on the current path
  const getAuthMode = () => {
    if (location.pathname === "/login") return "signin";
    if (location.pathname === "/forgot-password") return "forgot-password";
    if (location.pathname.startsWith("/signup")) return "signup";
    return "signin"; // default
  };

  const [authMode, setAuthMode] = useState<AuthMode>(getAuthMode());

  // Update auth mode when path changes
  useEffect(() => {
    setAuthMode(getAuthMode());
    // Clear error when changing modes
    setError("");
  }, [location.pathname]);

  // Set user type based on signup path
  useEffect(() => {
    if (location.pathname === "/signup/nonprofit") {
      setUserType("nonprofit");
    } else if (location.pathname === "/signup/corporate") {
      setUserType("corporate");
    } else if (location.pathname === "/signup/host") {
      setUserType("host");
    }
  }, [location.pathname]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    return password.length >= 6;
  };

  const getEmailError = () => {
    if (!touched.email) return "";
    if (!email) return "Email is required";
    if (!validateEmail(email)) return "Please enter a valid email address";
    return "";
  };

  const getPasswordError = () => {
    if (!touched.password || authMode === "forgot-password") return "";
    if (!password) return "Password is required";
    if (!validatePassword(password)) return "Password must be at least 6 characters";
    return "";
  };

  const handleBlur = (field: 'email' | 'password') => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleError = (error: any) => {
    if (error?.error) {
      setError(error.error);
    } else if (error?.message) {
      setError(error.message);
    } else if (typeof error === 'string') {
      setError(error);
    } else {
      setError("Something went wrong. Please try again.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Set all fields as touched when submitting
    setTouched({ email: true, password: true });

    // Validate all fields
    const emailError = getEmailError();
    const passwordError = getPasswordError();

    if (emailError) {
      setError(emailError);
      return;
    }

    if (passwordError && authMode !== "forgot-password") {
      setError(passwordError);
      return;
    }

    setIsLoading(true);

    try {
      if (authMode === "signin") {
        const { data, error } = await signIn(email, password);
        if (error) {
          handleError(error);
          return;
        }
        if (data?.session) {
          setSuccess("Successfully signed in!");
          window.location.reload();
          return
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else if (authMode === "signup") {
        try {
          const { data, error } = await signUp(email, password, { type: userType }, inviteCode);
          if (error) {
            handleError(error);
            return;
          }
          if (data?.session) {
            handleRedirect(redirectPath);
          } else {
            setSuccess("Please check your email for verification!");
          }
        } catch (error) {
          handleError(error);
        }
      } else if (authMode === "forgot-password") {
        try {
          await requestPasswordReset(email);
          setSuccess("Password reset email sent. Please check your inbox.");
          toast({
            title: "Password Reset Email Sent",
            description: "Check your email for the password reset link",
          });
        } catch (error) {
          handleError(error);
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setAuthMode(newMode);
    setError("");
    setEmail("");
    setPassword("");
  };

  const getDefaultUserType = () => {
    const path = window.location.pathname.toLowerCase();
    if (path.includes("/signup/nonprofit")) return "nonprofit";
    if (path.includes("/signup/host")) return "host";
    if (path.includes("/signup/corporate")) return "corporate";
    return null;
  };

  const handleJoinWaitlist = async () => {
    if (!waitlistForm.email || !waitlistForm.phone || !waitlistForm.userType) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all required fields.",
      })
      return
    }

    setIsWaitlistLoading(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/waitlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: waitlistForm.name,
          email: waitlistForm.email,
          phone: `${waitlistForm.countryCode}${waitlistForm.phone}`,
          user_type: waitlistForm.userType,
          remarks: waitlistForm.remarks,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || "Failed to join waitlist")
      }

      toast({
        title: "Success",
        description: "Thank you for your interest! We'll review your request and get back to you soon.",
      })
      
      // Reset form and close dialog
      setWaitlistForm({
        name: "",
        email: "",
        phone: "",
        countryCode: "+65",
        userType: "",
        remarks: "",
      })
      setIsWaitlistDialogOpen(false)
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to join waitlist. Please try again.",
      })
    }
    setIsWaitlistLoading(false)
  }

  return (
    <div className="flex min-h-screen">
      <div className="relative flex-1 hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1593113598332-cd288d649433?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
          alt="Volunteers working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/40" /> {/* Overlay */}
        <div className="absolute inset-0 flex flex-col justify-center items-center px-12">
          <div className="w-full max-w-2xl p-8 rounded-xl text-center backdrop-blur-md bg-white/10">
            <h2 className="text-3xl font-bold mb-6 text-white">
              {userType === "nonprofit"
                ? "Supercharge Your Nonprofit with AI"
                : userType === "corporate"
                ? "Empower Your CSR Journey"
                : "Create Meaningful Connections"}
            </h2>
            
            {userType === "nonprofit" ? (
              <div className="space-y-8">
                <p className="text-xl text-white opacity-90">
                No more multiple tools & Subscriptions. Just 
                </p>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">1</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">AI Growth Engine </h3>
                      <p className="text-white/90">Generate donor emails, impact reports, grant proposals, and fundraising campaigns in minutes.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">2</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Mission Hub </h3>
                      <p className="text-white/90">A central space to manage corporate partnerships, track volunteer engagement, and showcase opportunities.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">3</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Impact Assistant</h3>
                      <p className="text-white/90">AI-powered chatbot to answer FAQs, capture leads, and automate volunteer & donor communication 24/7.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">4</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Seamless Volunteer Coordination</h3>
                      <p className="text-white/90">Automate signups, reminders, and follow-ups to reduce admin work by 75%.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">5</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Unified Engagement Dashboard </h3>
                      <p className="text-white/90">Real-time insights into event participation, volunteer retention, and fundraising success.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : userType === "corporate" ? (
              <div className="space-y-8">
                <p className="text-xl text-white opacity-90">
                  Transform your CSR initiatives with meaningful volunteer opportunities that align with your company's values.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">1</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Find Perfect Matches</h3>
                      <p className="text-white/90">Discover and connect with nonprofits that align with your company's CSR goals and values.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">2</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Streamline Employee Engagement</h3>
                      <p className="text-white/90">Easily manage and track employee participation in volunteer activities and CSR programs.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">3</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Measure Social Impact</h3>
                      <p className="text-white/90">Access detailed analytics and reports to quantify and showcase your company's social impact.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <p className="text-xl text-white opacity-90">
                  Facilitate meaningful connections between nonprofits and corporations to create lasting social impact.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">1</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Bridge the Gap</h3>
                      <p className="text-white/90">Connect nonprofits with corporate partners to create successful CSR partnerships.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">2</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Manage Events</h3>
                      <p className="text-white/90">Coordinate and oversee volunteer events to ensure smooth execution and maximum impact.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">3</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-medium text-lg text-white">Drive Success</h3>
                      <p className="text-white/90">Provide support and guidance to ensure successful outcomes for all stakeholders.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-12 text-center">
              <p className="text-sm text-white/90">
                By signing up, you agree to our{" "}
                <a href="/legal/terms" className="text-white hover:underline font-medium">Terms of Service</a>
                {" "}and{" "}
                <a href="/legal/privacy" className="text-white hover:underline font-medium">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center flex-1 px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="w-full max-w-sm mx-auto lg:w-96">
          <div className="flex flex-col items-center">
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              {authMode === "signin"
                ? "Sign in to your account"
                : authMode === "signup"
                ? "Create your account"
                : "Reset your password"}
            </h2>
            {authMode === "signup" && (
              <div className="w-full mt-6" hidden={true}>
                <label className="block text-sm font-medium text-gray-700">
                  I am a
                </label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <button
                    type="button"
                    className={`${
                      userType === "nonprofit"
                        ? "border-indigo-500 ring-2 ring-indigo-500"
                        : "border-gray-300"
                    } relative px-3 py-2 text-sm font-medium text-gray-700 bg-white border rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                    onClick={() => setUserType("nonprofit")}
                  >
                    Nonprofit
                  </button>
                  <button
                    type="button"
                    className={`${
                      userType === "corporate"
                        ? "border-indigo-500 ring-2 ring-indigo-500"
                        : "border-gray-300"
                    } relative px-3 py-2 text-sm font-medium text-gray-700 bg-white border rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                    onClick={() => setUserType("corporate")}
                  >
                    Corporate
                  </button>
                  <button
                    type="button"
                    className={`${
                      userType === "host"
                        ? "border-indigo-500 ring-2 ring-indigo-500"
                        : "border-gray-300"
                    } relative px-3 py-2 text-sm font-medium text-gray-700 bg-white border rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                    onClick={() => setUserType("host")}
                  >
                    Host
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8">
            <div className="mt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 rounded-lg bg-red-50 border-2 border-red-500 text-red-700 text-sm flex items-start">
                    <div className="shrink-0 w-5 h-5 mr-2 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 font-medium">
                      {error}
                    </div>
                  </div>
                )}
                {success && (
                  <div className="p-4 rounded-lg bg-green-50 border-2 border-green-500 text-green-700 text-sm flex items-start">
                    <div className="shrink-0 w-5 h-5 mr-2 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 font-medium">
                      {success}
                    </div>
                  </div>
                )}
                {authMode === "signup" && (
                  <div className="space-y-2">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Name
                    </label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <div className="mt-1">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      onBlur={() => handleBlur('email')}
                      className={cn(
                        "block w-full",
                        getEmailError() && "border-destructive"
                      )}
                    />
                    {getEmailError() && (
                      <div className="mt-1.5 flex items-center text-sm text-red-600">
                        <svg className="mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        {getEmailError()}
                      </div>
                    )}
                  </div>
                </div>
                {authMode !== "forgot-password" && (
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Password
                    </label>
                    <div className="mt-1">
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete={authMode === "signin" ? "current-password" : "new-password"}
                        required
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                        }}
                        onBlur={() => handleBlur('password')}
                        className={cn(
                          "block w-full",
                          getPasswordError() && "border-destructive"
                        )}
                      />
                      {getPasswordError() && (
                        <div className="mt-1.5 flex items-center text-sm text-red-600">
                          <svg className="mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                          {getPasswordError()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {authMode === "signup" && (
                  <div className="space-y-2">
                    <label
                      htmlFor="inviteCode"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Invite Code
                    </label>
                    <Input
                      id="inviteCode"
                      placeholder="Enter your invite code"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="pt-2 space-y-4">
                  {authMode === "signup" ? (
                    <>
                      <Button className="w-full" type="submit" disabled={isLoading}>
                        {isLoading && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Create account
                      </Button>
                      <div className="text-center">
                        <Dialog open={isWaitlistDialogOpen} onOpenChange={setIsWaitlistDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="link" className="text-sm text-muted-foreground">
                              Don't have an invite code? Request an Invite
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[425px]">
                            <DialogHeader>
                              <DialogTitle>Request an Invite</DialogTitle>
                              <DialogDescription>
                                Join our waitlist to get early access to CauseConnect. We're currently in beta and are carefully onboarding new users to ensure the best experience.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                              <div className="grid gap-2">
                                <label
                                  htmlFor="waitlist-name"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Name
                                </label>
                                <Input
                                  id="waitlist-name"
                                  placeholder="Full name"
                                  value={waitlistForm.name}
                                  onChange={(e) => setWaitlistForm(prev => ({ ...prev, name: e.target.value }))}
                                  required
                                />
                              </div>
                              <div className="grid gap-2">
                                <label
                                  htmlFor="waitlist-email"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Email
                                </label>
                                <Input
                                  id="waitlist-email"
                                  type="email"
                                  placeholder="name@example.com"
                                  value={waitlistForm.email}
                                  onChange={(e) => setWaitlistForm(prev => ({ ...prev, email: e.target.value }))}
                                  required
                                />
                              </div>
                              <div className="grid gap-2">
                                <label
                                  htmlFor="waitlist-phone"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Phone
                                </label>
                                <div className="flex gap-2">
                                  <Select
                                    value={waitlistForm.countryCode}
                                    onValueChange={(value) => setWaitlistForm(prev => ({ ...prev, countryCode: value }))}
                                  >
                                    <SelectTrigger className="w-[100px]">
                                      <SelectValue placeholder="Code" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="+65">+65</SelectItem>
                                      <SelectItem value="+60">+60</SelectItem>
                                      <SelectItem value="+62">+62</SelectItem>
                                      <SelectItem value="+63">+63</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    id="waitlist-phone"
                                    type="tel"
                                    placeholder="Phone number"
                                    value={waitlistForm.phone}
                                    onChange={(e) => setWaitlistForm(prev => ({ ...prev, phone: e.target.value }))}
                                    required
                                  />
                                </div>
                              </div>
                              <div className="grid gap-2">
                                <label
                                  htmlFor="waitlist-type"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Who are you?
                                </label>
                                <Select
                                  value={waitlistForm.userType}
                                  onValueChange={(value) => setWaitlistForm(prev => ({ ...prev, userType: value }))}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select your role" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="nonprofit">Non-profit Organization</SelectItem>
                                    <SelectItem value="corporate">Corporate</SelectItem>
                                    <SelectItem value="host">Event Host</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="grid gap-2">
                                <label
                                  htmlFor="waitlist-remarks"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  What are you looking for? (Optional)
                                </label>
                                <textarea
                                  id="waitlist-remarks"
                                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                  placeholder="Tell us what you hope to achieve with CauseConnect..."
                                  value={waitlistForm.remarks}
                                  onChange={(e) => setWaitlistForm(prev => ({ ...prev, remarks: e.target.value }))}
                                />
                              </div>
                            </div>
                            <Button 
                              onClick={handleJoinWaitlist} 
                              disabled={isWaitlistLoading}
                              className="w-full"
                            >
                              {isWaitlistLoading && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              )}
                              Request Invite
                            </Button>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </>
                  ) : (
                    <Button className="w-full" disabled={isLoading}>
                      {isLoading && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      {authMode === "signin" ? "Sign in" : 
                       authMode === "signup" ? "Create account" : 
                       "Send reset link"}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">Or</span>
              </div>
            </div>

            <div className="mt-6 space-y-4 text-sm">
              {authMode === "signin" && (
                <>
                  <div className="text-center">
                    <button
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                      onClick={() => switchMode("signup")}
                    >
                      Don't have an account? Sign up
                    </button>
                  </div>
                  <div className="text-center">
                    <button
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                      onClick={() => switchMode("forgot-password")}
                    >
                      Forgot your password?
                    </button>
                  </div>
                </>
              )}
              {(authMode === "signup" || authMode === "forgot-password") && (
                <div className="text-center">
                  <button
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                    onClick={() => switchMode("signin")}
                  >
                    Already have an account? Sign in
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPages;
